import * as React from 'react';
import { Bestellung } from 'api/bestellung';
import { BestellungPosition, changeBestellungPositionMenge } from 'api/bestellungPosition';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { TextField } from '@fluentui/react/lib/TextField';
import { formatDecimal, parseDecimal } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useArtikelDetail } from 'api/artikelDetail';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';

const styles = mergeStyleSets({
    changeMengeGrid: {
        marginTop: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px 120px 120px',
        gridRowGap: 10,
        gridColumnGap: 10,
    },
    mengeLabel: {
        marginTop: 6,
    },
    menge: {
        textAlign: 'right',
    },
});

interface ChangeMenge {
    error: string | null;
    newMenge: number;
    newMengeS: string;
    newDifferenceS: string;
    newOffen: number;
    newOffenS: string;
    newOffenDifferenceS: string;
    newLArt: string;
}

const defaultChangeMenge: ChangeMenge = {
    error: null,
    newMenge: 0,
    newMengeS: '0',
    newDifferenceS: '0',
    newOffen: 0,
    newOffenS: '0',
    newOffenDifferenceS: '0',
    newLArt: '',
};

interface BestellungPositionChangeMengeProps {
    bestellung: Bestellung;
    bestellungPosition: BestellungPosition;
    onClose: (refetch: boolean) => void;
}

export const BestellungPositionChangeMenge: React.FC<BestellungPositionChangeMengeProps> = ({
    bestellungPosition,
    onClose,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const [changeMenge, setChangeMenge] = React.useState({
        ...defaultChangeMenge,
        newMenge: bestellungPosition.posMenge,
        newMengeS: formatDecimal(bestellungPosition.posMenge, 3),
        newDifferenceS: formatDecimal(0, 3),
        newOffen: bestellungPosition.offen,
        newOffenS: formatDecimal(bestellungPosition.offen, 3),
        newOffenDifferenceS: formatDecimal(0, 3),
        newLArt: bestellungPosition.lArt,
    });

    const artikelDetail = useArtikelDetail({
        artikelGruppeNr: bestellungPosition.artikelGruppeNr,
        artikelNr: bestellungPosition.artikelNr,
        includeArchiv: true,
    });

    const setValidatedChangeMengeLArt = (newMenge: number, newOffen: number, newLArt: string, updateOffen = false) => {
        let error: string | null = null;
        if (newMenge < 0) {
            error = 'Menge darf nicht < 0 sein.';
        }

        let updatedNewOffen = newOffen;

        if (updateOffen) {
            updatedNewOffen = bestellungPosition.offen + newMenge - bestellungPosition.posMenge;
        }

        if (updatedNewOffen < 0) {
            error = 'Offen darf nicht < 0 sein.';
        }

        setChangeMenge({
            error,
            newMenge,
            newMengeS: formatDecimal(newMenge, 3),
            newDifferenceS: formatDecimal(newMenge - (bestellungPosition?.posMenge || 0), 3),
            newOffen: updatedNewOffen,
            newOffenS: formatDecimal(updatedNewOffen, 3),
            newOffenDifferenceS: formatDecimal(updatedNewOffen - (bestellungPosition?.offen || 0), 3),
            newLArt,
        });
    };

    return (
        <ConfirmMessage
            title="Lieferart / Menge ändern"
            message={`Lieferart / Menge des Artikels ${
                artikelGruppenByNr[bestellungPosition?.artikelGruppeNr]?.text
            } | ${bestellungPosition?.artikelNr} ändern?`}
            confirmText="Lieferart / Menge ändern"
            cancelText="Abbruch"
            minWidth={820}
            content={
                <>
                    <ul>
                        <li>
                            <strong>Offene Bestellungen</strong> beim Artikel wird angepasst.
                        </li>
                        <li>
                            Wird die Lieferart auf <strong>Direkt</strong> geändert, wird{' '}
                            <strong>Offene Bestellungen</strong> in der Lagerverwaltung reduziert.
                        </li>
                        <li>
                            Wird die Lieferart auf <strong>Lager</strong> geändert, wird{' '}
                            <strong>Offene Bestellungen</strong> in der Lagerverwaltung erhöht.
                        </li>
                        <li>
                            Der <strong>Bestand</strong> in der Lagerverwaltung wird in keinem Fall verändert.
                        </li>
                    </ul>
                    {artikelDetail.isLoading ? (
                        <LoadingIndicator />
                    ) : (
                        <div className={styles.changeMengeGrid}>
                            <TextField
                                label="Lagerbestand"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.bestand || 0, 3)}
                            />
                            <TextField
                                label="offene Lief."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenLief || 0, 3)}
                            />
                            <TextField
                                label="offene Lief. (D)"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenLiefD || 0, 3)}
                            />
                            <TextField
                                label="offene Best."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenBest || 0, 3)}
                            />
                            <TextField
                                label="offene Best. (D)"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenBestD || 0, 3)}
                            />
                            <TextField
                                label="Reservierungen"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenRes || 0, 3)}
                            />

                            <div>
                                <LieferArt
                                    label="Aktuelle Lieferart"
                                    disabled
                                    allowEmpty
                                    value={bestellungPosition?.lArt || ''}
                                />
                            </div>
                            <div>
                                <LieferArt
                                    label="Neue Lieferart"
                                    allowEmpty
                                    value={changeMenge.newLArt || ''}
                                    menuPlacement="top"
                                    onChange={(newValue) => {
                                        setValidatedChangeMengeLArt(
                                            changeMenge.newMenge,
                                            changeMenge.newOffen,
                                            newValue,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                            <div />
                            <div />
                            <div />

                            <TextField
                                label="Aktuelle Menge"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(bestellungPosition?.posMenge || 0, 3)}
                            />
                            <TextField
                                label="Neue Menge"
                                inputClassName={styles.menge}
                                value={changeMenge.newMengeS}
                                onChange={(event, newValue) =>
                                    setChangeMenge({
                                        ...changeMenge,
                                        newMengeS: newValue === undefined ? '' : newValue,
                                    })
                                }
                                onBlur={(event): void => {
                                    setValidatedChangeMengeLArt(
                                        parseDecimal(event.target.value) || 0,
                                        changeMenge.newOffen,
                                        changeMenge.newLArt,
                                        true,
                                    );
                                }}
                            />
                            <div>
                                <TextField
                                    label="Differenz"
                                    inputClassName={styles.menge}
                                    value={changeMenge.newDifferenceS}
                                    onChange={(event, newValue) =>
                                        setChangeMenge({
                                            ...changeMenge,
                                            newDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        setValidatedChangeMengeLArt(
                                            (bestellungPosition?.posMenge || 0) +
                                                (parseDecimal(event.target.value) || 0),
                                            changeMenge.newOffen,
                                            changeMenge.newLArt,
                                            true,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                            <div />
                            <div />

                            <TextField
                                label="Aktuell Offen"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(bestellungPosition?.offen || 0, 3)}
                            />
                            <TextField
                                label="Neu Offen"
                                inputClassName={styles.menge}
                                value={changeMenge.newOffenS}
                                onChange={(event, newValue) =>
                                    setChangeMenge({
                                        ...changeMenge,
                                        newOffenS: newValue === undefined ? '' : newValue,
                                    })
                                }
                                onBlur={(event): void => {
                                    setValidatedChangeMengeLArt(
                                        changeMenge.newMenge,
                                        parseDecimal(event.target.value || '') || 0,
                                        changeMenge.newLArt,
                                    );
                                }}
                            />
                            <div>
                                <TextField
                                    label="Differenz"
                                    inputClassName={styles.menge}
                                    value={changeMenge.newOffenDifferenceS}
                                    onChange={(event, newValue) =>
                                        setChangeMenge({
                                            ...changeMenge,
                                            newOffenDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        setValidatedChangeMengeLArt(
                                            changeMenge.newMenge,
                                            (bestellungPosition?.offen || 0) + (parseDecimal(event.target.value) || 0),
                                            changeMenge.newLArt,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                        </div>
                    )}
                    {changeMenge.error && <div className={formStyles.error}>{changeMenge.error}</div>}
                </>
            }
            onConfirm={async () => {
                await changeBestellungPositionMenge(
                    {
                        ...bestellungPosition,
                        posMenge: changeMenge.newMenge,
                        // offen: bestellungPosition.offen + changeMenge.newMenge - bestellungPosition.posMenge,
                        offen: changeMenge.newOffen,
                        lArt: changeMenge.newLArt,
                    },
                    false,
                ).then(() => {
                    onClose(true);
                });
            }}
            confirmDisabled={
                !!changeMenge.error ||
                (changeMenge.newMenge === bestellungPosition?.posMenge &&
                    changeMenge.newOffen === bestellungPosition?.offen &&
                    changeMenge.newLArt === bestellungPosition?.lArt)
            }
            onCancel={() => onClose(false)}
        />
    );
};
